<template>
  <div class="content">
    <v-toolbar flat>
      <v-app-bar-nav-icon><v-icon>mdi-whistle-outline</v-icon></v-app-bar-nav-icon>
      <v-toolbar-title>
        <span v-on="canViewParentFederation ? { click: redirectToParentFederation } : {}" v-bind:class="{ link: canViewParentFederation }">{{ federation.name }}</span>
        ->
        <span v-on="canViewParentDiscipline ? { click: redirectToParentDiscipline } : {}" v-bind:class="{ link: canViewParentDiscipline }">{{ discipline.name }}</span>
        ->
        <span>{{ coachPublicName }}</span>
      </v-toolbar-title>
    </v-toolbar>

    <v-tabs v-model="tab">
      <v-tab to="teams"><v-icon class="tab-icon">mdi-account-group-outline</v-icon> Équipes</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" style="overflow: visible; display: block">
      <v-tab-item id="teams">
        <teams :coachId_prop="coachId" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { VTabs } from "vuetify/lib";
import { getDisciplineAsync, getIsManagerPartOfDisciplineAsync, getIsCoachPartOfDisciplineAsync } from "@/api/disciplinesApi";
import { getFederationByIdAsync, getIsUserPartOfFederationAsync } from "@/api/federationsApi";
import { getCoachPublicNameAsync } from "@/api/teamApi";
import PermissionsService from "@/services/PermissionsService";
import _ from "lodash";
import store from "@/store";
import AuthService from "@/services/AuthService";
import teams from "@/containers/CoachPanel/teams";

export default {
  components: {
    VTabs,
    teams,
  },

  props: {
    disciplineId_prop: [Number, String],
    federationId_prop: [Number, String],
    coachId_prop: [Number, String],
  },

  watch: {
    $route() {
      this.initPage();
    },
  },

  data() {
    return {
      isCurrentUserPartOfFederation: false,
      isCurrentUserManagerOfDiscipline: false,
      isCurrentUserCoachOfDiscipline: false,
      federationId: null,
      disciplineId: null,
      coachId: null,
      authService: AuthService,
      federation: {},
      discipline: {},
      coachPublicName: null,
      tab: null,
    };
  },

  computed: {
    canViewCurrentCoachPanel() {
      // Can view own discipline and is manager of current discipline
      let canViewOwnDisciplineAndIsManagerOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can view own discipline and is coach of current discipline
      let canViewOwnDisciplineAndIsCoachOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserCoachOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsManagerOfCurrentDiscipline || canViewOwnDisciplineAndIsCoachOfCurrentDiscipline || canViewAllDisciplines;
    },
    canViewParentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canViewParentFederation() {
      // Can view own federation and is part of current federation
      let canViewOwnFederationAndIsPartOfCurrentFederation = PermissionsService.FederationsPermissionsService.canViewOwnFederation && this.isCurrentUserPartOfFederation;
      // Can view all federations
      let canViewAllFederations = PermissionsService.FederationsPermissionsService.canViewAllFederations;
      return canViewOwnFederationAndIsPartOfCurrentFederation || canViewAllFederations;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : this.federationId_prop;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;
      this.coachId = this.$route.params.coachId ? this.$route.params.coachId : this.coachId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserCoachOfDiscipline = await getIsCoachPartOfDisciplineAsync(this.authService.userId, this.disciplineId);
      // Check if the current user is part of current discipline
      this.isCurrentUserManagerOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewCurrentCoachPanel) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      let coachPublicNameResult = await getCoachPublicNameAsync(this.coachId);
      this.coachPublicName = coachPublicNameResult.value;
      this.discipline = await getDisciplineAsync(this.disciplineId);
      this.federation = await getFederationByIdAsync(this.federationId);
    },

    // Redirect to parent federation
    redirectToParentFederation() {
      this.$router.push({
        name: "FederationPanel",
        params: {
          id: this.federation.id,
          tab: "users",
        },
      });
    },

    // Redirect to parent discipline
    redirectToParentDiscipline() {
      this.$router.push({
        name: "DisciplinePanel",
        params: {
          federationId: this.federationId,
          disciplineId: this.disciplineId,
          tab: "disciplineManagers",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.blue-ENE {
  color: rgb(15, 21, 71) !important;
}
.green-GCE {
  color: rgb(27, 138, 98) !important;
}
.tab-icon {
  padding-right: 5px;
}
.link {
  color: rgb(15, 21, 71);
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: rgb(15, 21, 71);
  cursor: pointer;

  &:hover {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgba(15, 21, 71, 0.5);
  }
  &:focus {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
  &:active {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
}
</style>
